import React, { useEffect, useRef } from 'react';
import { fabric } from 'fabric';
import { useStore } from '../store';

interface CanvasProps {
  onElementSelect: (type: string, id: string) => void;
  containerWidth?: number;
  containerHeight?: number;
  canvasWidth?: number;
  canvasHeight?: number;
  canvasLeft?: number;
  canvasTop?: number;
  objectBackground?: string;
  onCanvasReady?: (canvas: fabric.Canvas | null) => void;
}

export const Canvas: React.FC<CanvasProps> = ({
  onElementSelect,
  containerWidth = 500,
  containerHeight = 500,
  canvasWidth = 108,
  canvasHeight = 160,
  canvasLeft = 196,
  canvasTop = 190,
  objectBackground,
  onCanvasReady
}) => {
  const canvasWrapperRef = useRef<HTMLDivElement>(null);
  const fabricRef = useRef<fabric.Canvas | null>(null);
  const store = useStore();

  useEffect(() => {
    const canvasEl = document.createElement('canvas');
    canvasEl.width = canvasWidth;
    canvasEl.height = canvasHeight;
    
    if (canvasWrapperRef.current) {
      canvasWrapperRef.current.innerHTML = '';
      canvasWrapperRef.current.appendChild(canvasEl);
      
      const canvas = new fabric.Canvas(canvasEl, {
        preserveObjectStacking: true,
        stopContextMenu: true,
        selection: true,
        backgroundColor: store.transparentBackground ? 'transparent' : store.backgroundColor,
        renderOnAddRemove: true,
      });

      fabric.Object.prototype.set({
        borderColor: '#2196F3',
        cornerColor: '#2196F3',
        cornerSize: 12,
        transparentCorners: false,
        padding: 10,
      });

      const handleSelection = (e: fabric.IEvent) => {
        const obj = e.selected?.[0];
        if (obj?.data?.type && obj?.data?.id) {
          onElementSelect(obj.data.type, obj.data.id);
        }
      };

      canvas.on('selection:created', handleSelection);
      canvas.on('selection:updated', handleSelection);

      canvas.on('selection:cleared', () => {
        store.setActiveTextId(null);
      });

      canvas.on('object:moving', (e) => {
        const obj = e.target;
        if (!obj) return;
        
        if (obj.data?.id) {
          store.updateTextElement(obj.data.id, {
            position: {
              left: Math.round(obj.left ?? 0),
              top: Math.round(obj.top ?? 0),
            },
          });
        }
      });

      canvas.on('object:modified', (e) => {
        const obj = e.target;
        if (!obj?.data?.id) return;

        const updates: any = {
          position: {
            left: Math.round(obj.left ?? 0),
            top: Math.round(obj.top ?? 0),
          },
        };

        if (obj.type === 'text') {
          updates.size = Math.round(obj.fontSize! * (obj.scaleX || 1));
          updates.rotation = Math.round(obj.angle || 0);

          obj.set({
            fontSize: updates.size,
            scaleX: 1,
            scaleY: 1,
          });
        }

        store.updateTextElement(obj.data.id, updates);
        canvas.requestRenderAll();
      });
      
      if (onCanvasReady) {
        onCanvasReady(canvas);
      }
      
      store.setCanvasDimensions(canvasWidth, canvasHeight);
      fabricRef.current = canvas;
    }

    return () => {
      if (onCanvasReady) {
        onCanvasReady(null);
      }
      fabricRef.current?.dispose();
    };
  }, [canvasWidth, canvasHeight]);

  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    
    if (!store.transparentBackground) {
      canvas.setBackgroundColor(store.backgroundColor, () => canvas.requestRenderAll());
    } else {
      canvas.setBackgroundColor('transparent', () => canvas.requestRenderAll());
    }
  }, [store.backgroundColor, store.transparentBackground]);

  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    canvas.getObjects().forEach(obj => {
      if (obj.type === 'text') {
        const element = store.textElements.find(el => el.id === obj.data?.id);
        if (element) {
          obj.set({
            text: element.text,
            fontSize: element.size,
            fill: element.color,
            fontFamily: element.font,
            angle: element.rotation,
            left: element.position?.left ?? canvas.width! / 2,
            top: element.position?.top ?? canvas.height! / 2,
          });
        } else {
          canvas.remove(obj);
        }
      }
    });

    store.textElements.forEach((element) => {
      if (!canvas.getObjects().some(obj => obj.data?.id === element.id)) {
        const text = new fabric.Text(element.text, {
          left: element.position?.left ?? canvas.width! / 2,
          top: element.position?.top ?? canvas.height! / 2,
          originX: 'center',
          originY: 'center',
          fontSize: element.size,
          fill: element.color,
          fontFamily: element.font,
          angle: element.rotation,
          data: { type: 'text', id: element.id },
          hasControls: true,
          hasBorders: true,
          selectable: true,
          lockUniScaling: true,
        });
        canvas.add(text);
      }
    });

    const activeId = store.activeTextId;
    if (activeId) {
      const activeObject = canvas.getObjects().find(obj => obj.data?.id === activeId);
      if (activeObject && !activeObject.active) {
        canvas.setActiveObject(activeObject);
      }
    } else {
      canvas.discardActiveObject();
    }

    canvas.requestRenderAll();
  }, [store.textElements, store.activeTextId]);

  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    canvas.getObjects('image').forEach(obj => canvas.remove(obj));

    store.imageElements.forEach((element) => {
      fabric.Image.fromURL(element.url, (img) => {
        img.scaleToWidth(canvas.width! * 0.8);
        img.set({
          left: canvas.width! / 2,
          top: canvas.height! / 2,
          originX: 'center',
          originY: 'center',
          data: { type: 'photo', id: element.id },
          hasControls: true,
          hasBorders: true,
          selectable: true,
          lockUniScaling: true,
        });
        
        canvas.insertAt(img, 0);
        canvas.requestRenderAll();
      });
    });
  }, [store.imageElements]);

  return (
    <div 
      className="relative flex items-center justify-center bg-transparent rounded-lg overflow-hidden"
      style={{ 
        width: containerWidth, 
        height: containerHeight,
        position: 'relative'
      }}
    >
      {objectBackground && (
        <img
          src={objectBackground}
          alt="Object background"
          className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none"
          style={{ userSelect: 'none' }}
          crossOrigin="anonymous"
          onError={(e) => {
            console.error('Failed to load object background:', e);
            e.currentTarget.style.display = 'none';
          }}
        />
      )}
      <div 
        ref={canvasWrapperRef} 
        className="absolute"
        style={{
          position: 'absolute',
          left: `${canvasLeft}px`,
          top: `${canvasTop}px`,
          width: `${canvasWidth}px`,
          height: `${canvasHeight}px`,
          backgroundColor: store.transparentBackground ? 'transparent' : undefined
        }}
      >
        <canvas className="rounded-lg" />
      </div>
    </div>
  );
};